import React from 'react';

import { Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { checkIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import { OptionType } from '../FlexySelect';

export type BaseItemReaderProps = {
    label: string;
    baseline?: string;
    value: OptionType['value'];
    isChecked?: boolean;
};

export type SelectItemReaderProps = BaseItemReaderProps & {
    fullWidth?: boolean;
    design?: 'block' | 'inline' | 'basic';
};

/**
 * Composant de lecture seule pour afficher un élément de sélection
 *
 * Affiche un élément avec son label, baseline optionnelle et état de sélection.
 * Trois designs disponibles selon le contexte d'utilisation.
 * Version lecture seule sans interaction utilisateur.
 *
 * @param label - Texte principal affiché
 * @param baseline - Texte secondaire optionnel (masqué en mode 'basic')
 * @param value - Valeur associée à l'élément
 * @param isChecked - État de sélection de l'élément
 * @param fullWidth - Si true, occupe toute la largeur disponible
 * @param design - Mode d'affichage : 'inline' (horizontal), 'block' (vertical), 'basic' (minimal)
 */
export const SelectItemReader: React.FunctionComponent<SelectItemReaderProps> = function ({
    label,
    baseline,
    value,
    fullWidth = true,
    design = 'inline',
    isChecked = false,
}) {
    const theme = useTheme();

    const designConfig = {
        inline: {
            containerDirection: 'row' as const,
            containerHeight: '40px',
            containerPadding: '4px 16px',
            iconSize: 14,
            iconWithShape: false,
            iconGap: 1,
        },
        block: {
            containerDirection: 'column' as const,
            containerHeight: '149px',
            containerPadding: '16px',
            iconSize: 20,
            iconWithShape: true,
            iconGap: 1,
        },
        basic: {
            containerDirection: 'row' as const,
            containerHeight: '48px',
            containerPadding: '4px 16px',
            iconSize: 14,
            iconWithShape: false,
            iconGap: 1,
        },
    };

    const config = designConfig[design];

    const getItemStyles = () => {
        if (isChecked) {
            return {
                borderColor: theme.palette.grey[400],
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.text.primary,
                opacity: 1,
            };
        }

        return {
            borderColor: theme.palette.grey[300],
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.disabled,
            opacity: 0.6,
        };
    };

    const iconColor = isChecked ? theme.palette.text.primary : theme.palette.text.disabled;

    return (
        <Box
            key={`${value}`}
            sx={{
                width: fullWidth ? '100%' : 'fit-content',
                height: config.containerHeight,
                padding: config.containerPadding,
                border: '1px solid',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: config.containerDirection,
                justifyContent: 'center',
                alignItems: 'center',
                ...getItemStyles(),
            }}
            className={isChecked ? 'selectItem-checked' : 'selectItem-unchecked'}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: config.containerDirection,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: config.iconGap,
                }}
            >
                {isChecked && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: config.iconSize,
                            height: config.iconSize,
                            lineHeight: 1,
                            '& svg': {
                                width: `${config.iconSize}px !important`,
                                height: `${config.iconSize}px !important`,
                                fontSize: `${config.iconSize}px !important`,
                                verticalAlign: 'baseline',
                            },
                        }}
                    >
                        <FaOptionIcon
                            icon={[
                                'fasl',
                                (checkIcon.props.icon as unknown as IconDefinition).iconName,
                            ]}
                            size="xs"
                            color={iconColor}
                            withShape={config.iconWithShape}
                            shapeSize={config.iconWithShape ? config.iconSize + 8 : undefined}
                        />
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="l2dcaption"
                        sx={{
                            textAlign: 'center',
                            color: 'inherit',
                            lineHeight: 1.2,
                            ...(design === 'basic' && { whiteSpace: 'nowrap' }),
                        }}
                    >
                        {label}
                    </Typography>

                    {baseline && design !== 'basic' && (
                        <Typography
                            sx={{
                                fontSize: '13px',
                                lineHeight: '16px',
                                marginTop: '4px',
                                textAlign: 'center',
                                color: 'inherit',
                                opacity: 0.8,
                            }}
                        >
                            {baseline}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
